/**
 * SavePolicy (AJIP-16.02.004 · WS5)
 * ---------------------------------------------------------------------
 * Configuration-driven decision layer for whether a save should occur.
 * Prevents excessive writes, duplicate records, race conditions and
 * unauthorised persistence.
 *
 * Construction
 *   new SavePolicy({
 *     saveOnTransition        = true,   // on every accepted transition
 *     saveOnPause             = true,   // on suspend()
 *     saveOnCheckpoint        = true,   // on explicit checkpoint()
 *     saveOnExplicitUserAction = true,  // reserved for future UI
 *     debounceMs              = 250,    // trailing-edge debounce
 *   })
 *
 * Decision
 *   shouldPersist({ triggerKind, currentSignature, lastSavedSignature })
 *     * triggerKind: 'transition' | 'pause' | 'checkpoint' | 'user'
 *     * signatures: an opaque string; if equal, save is a no-op.
 *     * Debounce is applied via debounceAllowed(nowMs, journeyId).
 */
class SavePolicy {
  constructor({
    saveOnTransition = true,
    saveOnPause = true,
    saveOnCheckpoint = true,
    saveOnExplicitUserAction = true,
    debounceMs = 250,
  } = {}) {
    this._flags = Object.freeze({
      saveOnTransition,
      saveOnPause,
      saveOnCheckpoint,
      saveOnExplicitUserAction,
    });
    this._debounceMs = debounceMs;
    this._lastSaveAtByKey = new Map();
  }

  get flags() { return this._flags; }
  get debounceMs() { return this._debounceMs; }

  triggerAllowed(triggerKind) {
    switch (triggerKind) {
      case 'transition': return this._flags.saveOnTransition;
      case 'pause':      return this._flags.saveOnPause;
      case 'checkpoint': return this._flags.saveOnCheckpoint;
      case 'user':       return this._flags.saveOnExplicitUserAction;
      default:           return false;
    }
  }

  signatureUnchanged(currentSignature, lastSavedSignature) {
    return currentSignature !== undefined
      && lastSavedSignature !== undefined
      && currentSignature === lastSavedSignature;
  }

  debounceAllowed(nowMs, key) {
    const last = this._lastSaveAtByKey.get(key);
    if (last === undefined) return true;
    return (nowMs - last) >= this._debounceMs;
  }

  recordSave(nowMs, key) {
    this._lastSaveAtByKey.set(key, nowMs);
  }

  shouldPersist({ triggerKind, currentSignature, lastSavedSignature, nowMs, key }) {
    if (!this.triggerAllowed(triggerKind)) return { save: false, reason: 'trigger_disabled' };
    if (this.signatureUnchanged(currentSignature, lastSavedSignature)) {
      return { save: false, reason: 'no_change' };
    }
    if (!this.debounceAllowed(nowMs, key)) return { save: false, reason: 'debounced' };
    return { save: true, reason: 'ok' };
  }
}

export { SavePolicy };
